import React from 'react';
import { useTranslation } from 'react-i18next';
import { FoldHorizontal, History, MessageSquare, MoveHorizontal, Share2, PanelLeft, TableOfContents } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import AvatarStack from '../ui/AvatarStack';
import useScopeItemHistory from '../git/useScopeItemHistory';
import ToolbarBreadcrumb from './ToolbarBreadcrumb';
import DocumentMoreMenu from './DocumentMoreMenu';
import { DOC_FLAGS } from './docFlags';
import { formatCount, relativeAge } from './docMetaFormat';

/** "Edited 4 d ago · 1,240 words" — either half alone when the other is unknown. */
function MetaLabel({ meta }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const age = relativeAge(meta.editedAt, meta.now, t, lang);
  const parts = [
    age ? t('docEditor.meta.edited', { age }) : null,
    meta.words != null ? t('docEditor.meta.words', { count: meta.words, formatted: formatCount(meta.words, lang) }) : null,
  ].filter(Boolean);
  if (!parts.length) return null;
  return (
    <span className="doc-toolbar-meta" data-testid="doc-toolbar-meta">
      {parts.join(' · ')}
    </span>
  );
}

/** The git history clock (today's history panel). Without git sync it stays, explained and inert. */
function HistoryButton({ scopeKey, documentId, title }) {
  const { t } = useTranslation();
  const history = useScopeItemHistory({ scopeKey, itemId: documentId, itemName: title, kind: 'page' });
  return (
    <>
      <Tooltip
        content={history.available ? t('docEditor.toolbar.history') : t('docEditor.toolbar.historyUnavailable')}
        side="bottom"
      >
        <button
          type="button"
          className="doc-tbtn doc-tbtn--icon"
          aria-label={t('docEditor.toolbar.history')}
          aria-disabled={!history.available}
          data-testid="page-history-button"
          onClick={history.available ? history.open : undefined}
        >
          <History size={16} strokeWidth={1.6} aria-hidden />
        </button>
      </Tooltip>
      {history.modal}
    </>
  );
}

/**
 * The 48 px document toolbar under the tab strip (card 2.1): breadcrumb on
 * the left; on the right "Edited … · N words", Version history, Comments
 * (behind DOC_FLAGS.comments — there is no comment UI yet), Share (primary
 * tint: copies the page's link), Full width (per document, useFullWidth), the
 * outline toggle (card 2.4) and More (card 3.1). Below 900 px a sidebar button leads the row (card 2.6).
 */
export default function DocumentToolbar({
  documentId,
  scope,
  scopeKey,
  title,
  ancestors,
  meta,
  actions,
  viewers,
  narrowSidebar,
  onToggleSidebar,
  outlineOpen,
  onToggleOutline,
  fullWidth = false,
  onToggleFullWidth,
}) {
  const { t } = useTranslation();
  return (
    <div className="doc-toolbar" data-testid="doc-toolbar">
      {narrowSidebar && (
        <Tooltip content={t('docEditor.toolbar.sidebar')} side="bottom">
          <button
            type="button"
            className="doc-tbtn doc-tbtn--icon"
            aria-label={t('docEditor.toolbar.sidebar')}
            data-docs-sidebar-toggle
            data-testid="doc-sidebar-toggle"
            onClick={onToggleSidebar}
          >
            <PanelLeft size={16} strokeWidth={1.6} aria-hidden />
          </button>
        </Tooltip>
      )}
      <ToolbarBreadcrumb ancestors={ancestors} scope={scope} title={title} />
      <div className="doc-toolbar-spacer" />
      <MetaLabel meta={meta} />
      <AvatarStack members={viewers} />
      <HistoryButton scopeKey={scopeKey} documentId={documentId} title={title} />
      {DOC_FLAGS.comments && (
        <Tooltip content={t('docEditor.toolbar.comments')} side="bottom">
          <button type="button" className="doc-tbtn doc-tbtn--icon" aria-label={t('docEditor.toolbar.comments')}>
            <MessageSquare size={16} strokeWidth={1.6} aria-hidden />
          </button>
        </Tooltip>
      )}
      <Tooltip content={t('docEditor.toolbar.shareHint')} side="bottom">
        <button type="button" className="doc-tbtn doc-tbtn--primary" onClick={actions.onShare} data-testid="doc-share-button">
          <Share2 size={16} strokeWidth={1.6} aria-hidden />
          <span>{t('docEditor.toolbar.share')}</span>
        </button>
      </Tooltip>
      {onToggleFullWidth && (
        <Tooltip content={fullWidth ? t('docEditor.toolbar.standardWidth') : t('docEditor.toolbar.fullWidth')} side="bottom">
          <button
            type="button"
            className={`doc-tbtn doc-tbtn--icon${fullWidth ? ' is-on' : ''}`}
            aria-label={t('docEditor.toolbar.widthToggle')}
            aria-pressed={fullWidth}
            data-testid="doc-full-width-toggle"
            onClick={onToggleFullWidth}
          >
            {fullWidth ? (
              <FoldHorizontal size={16} strokeWidth={1.6} aria-hidden />
            ) : (
              <MoveHorizontal size={16} strokeWidth={1.6} aria-hidden />
            )}
          </button>
        </Tooltip>
      )}
      <Tooltip content={outlineOpen ? t('docEditor.toolbar.hideOutline') : t('docEditor.toolbar.showOutline')} side="bottom">
        <button
          type="button"
          className={`doc-tbtn doc-tbtn--icon${outlineOpen ? ' is-on' : ''}`}
          aria-label={t('docEditor.toolbar.outline')}
          aria-pressed={outlineOpen}
          data-testid="doc-outline-toggle"
          onClick={onToggleOutline}
        >
          <TableOfContents size={16} strokeWidth={1.6} aria-hidden />
        </button>
      </Tooltip>
      <DocumentMoreMenu
        pinned={actions.pinned}
        canMove
        canPin
        onAction={actions.onAction}
        onExport={actions.onExport}
      />
    </div>
  );
}
